
import {Component, Prop, Vue} from "vue-property-decorator";

import UserLogin from "@/models/User/UserLogin";
import {LOGIN_USER, TRANSLATIONS_FETCH,} from "@/store/ActionTypes";
import {AuthStates} from "@/store/modules/AccountModule";
import {mapState} from "vuex";

import router from "@/router";
import CustomersRepository from "@/utils/API/CustomersRepository";
import vFooter from "@/components/vFooter.vue";
import {UserTypes} from "@/models/User/UserTypes";
import UserRepository from "@/utils/API/UserRepository";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import {Snotify} from "vue-snotify";
import SystemConfigurationRepository from "@/utils/API/SystemConfigurationRepository";
import {NutrientSourceList} from "@/models/NutrientSource/NutrientSourceList";
import SignupDto from "@/models/Restaurants/SignupDto";
import GeneralRepository from "@/utils/API/GeneralRepository";
import Package from "@/models/Subscriptions/SubscriptionPackages/Package";
import AccountActivationRepository from "@/utils/API/AccountActivationRepository";
import {AxiosError} from "axios";
import {DIGIT_ONLY_REGEX} from "@/utils/constants";

@Component({
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
  components: {
    vFooter,
  },
})
class Login extends Vue {
  @Prop() forgotPassword?: boolean;

  localizationState: any;
  accountStoreState: any;

  nutrientSources: NutrientSourceList[] = [];
  trialPeriodInDays: Number = 0;

  countries: any[] = [];
  industriesList: any[] = [];
  selectedCountry: any = null;
  selectedIndustry: any = null;
  isVerificationMode: boolean = false;
  verificationError: string = '';
  isVerifying: boolean = false;
  verificationCharacters: string[] = Array(6).fill("");
  verificationCode: string = '';
  isResendDisabled: boolean = false;
  resendCountdown: number = 0;
  resendTimer: number | null = null;

  user: UserLogin = { email: "", password: "", rememberMe: false };
  signupDto: SignupDto = {
    name: "",
    phone: "",
    ownerEmail: "",
    ownerPassword: "",
    confirmOwnerPassword: "",
    ownerFirstName: "",
    ownerLastName: "",
    referrer: null,
    countryId: null,
    industryId: null,
    hasRequestedNewsLetter: false,
    shouldSkipFreeTrial : true,
    selectedPackage: null,
    subscriptionRecurrence: null,
    marketingParams: {
      source: '',
      medium: '',
      campaign: '',
      term: '',
      gclid: ''
    }
  };

  forgotPasswordEmail: string = "";
  invalidEmail: boolean = false;
  correctEmail: boolean = false;

  termsConditions: boolean = false;

  invalidPhone: boolean = false;
  phoneTouched: boolean = false;
  displayNutrientSources: boolean = false;

  async getPackageInfo(){
    const query = this.$route.query;

    this.signupDto.marketingParams.source = (query.utm_source as string) || '';
    this.signupDto.marketingParams.medium = (query.utm_medium as string) || '';
    this.signupDto.marketingParams.campaign = (query.utm_campaign as string) || '';
    this.signupDto.marketingParams.term = (query.utm_term as string) || '';
    this.signupDto.marketingParams.gclid = (query.gclid as string) || '';

    // Check the route's params for a package
    const selectedPackage = query.package as string;
    // Check the route's params for a recurrence
    const recurrence = query.freq as string;

    // If the url has a package and recurrence then the packages are fetched accordingly.
    if(selectedPackage && recurrence && this.$route.name === "create"){
      this.signupDto.selectedPackage = selectedPackage;
      this.signupDto.subscriptionRecurrence = recurrence;
      const response = await SubscriptionRepository.getByFriendlyName(selectedPackage, recurrence);
      const packages: Package[] = response.data;

      packages.forEach((pkg) => {
        this.displayNutrientSources =  pkg.availableNutrientSources.length <= 2;
      });
    }
    else if(this.isFreeTrialMode){
      this.displayNutrientSources = true;
    }
  }

  goToForgot() {
    this.invalidEmail = false;
    this.correctEmail = false;
    this.forgotPasswordEmail = "";

    this.$router.push("/forgot");
  }

  attemptResetPassword() {
    this.$validator.validateAll("resetPassword").then((result) => {
      if (result) {
        this.correctEmail = false;
        this.invalidEmail = false;
        UserRepository.requestResetPassword({ email: this.forgotPasswordEmail })
          .then(() => {
            this.correctEmail = true;
          })
          .catch(() => {
            this.invalidEmail = true;
          });
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  goBackToLogin(): void {
    this.isVerificationMode = false;

    this.verificationCharacters = Array(6).fill("");
    this.verificationCode = "";
    this.verificationError = "";

    this.clearSignUpData();

    router.push("/");
  }

  clearSignUpData() {
    this.signupDto.name = "";
    this.signupDto.phone = "";
    this.signupDto.ownerEmail = "";
    this.signupDto.ownerPassword = "";
    this.signupDto.confirmOwnerPassword = "";
    this.signupDto.ownerFirstName = "";
    this.signupDto.ownerLastName = "";
    this.signupDto.referrer = null;
    this.signupDto.countryId = null;
    this.signupDto.industryId = null;
    this.signupDto.hasRequestedNewsLetter = false;
    this.termsConditions = false;
  }

  createAccount() {
    this.phoneTouched = true;
    this.$validator.validateAll("accCreate").then((result) => {
      if (result && !this.invalidPhone) {
        if (!this.termsConditions) {
          this.$snotify.error(
            this.translations.get("login_accept_terms_message")
          );
          return;
        }

        this.$snotify.async(
          this.translations.get("login_creating"),
          () => <Promise<Snotify>>new Promise((resolve, reject) => {
            this.signupDto.countryId = this.selectedCountry.id;
            this.signupDto.industryId = this.selectedIndustry.id;
            this.signupDto.shouldSkipFreeTrial = !this.isFreeTrialMode;
            CustomersRepository.signup(this.signupDto)
              .then(() => {
                if(!this.isFreeTrialMode){
                  // Instead of redirecting, show verification screen
                  this.isVerificationMode = true;
                  this.$snotify.success(this.translations.get("verification_code_sent"));

                  //window.location = process.env.VUE_APP_DIRECT_SIGNUP_THANKYOUPAGE_URL;
                }
                else{
                  window.location = process.env.VUE_APP_THANKYOUPAGE_URL;
                }
              })
              .catch((error) => {
                let message = "";
                if (error.response) {
                  message =
                    error.response.data.errorMessages[0].userVisibleMessage;
                }
                else {
                  message = this.translations.get("network_error");
                }
                reject({ body: message });
              });
          }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  async handleVerifyCode() {
    if (!this.verificationCode) {
      this.verificationError = 'Verification code is required';
      return;
    }

    try {
      this.verificationError = '';

      if (DIGIT_ONLY_REGEX.test(this.verificationCode)) {
        this.verificationError = this.translations.get('verification_code_invalid');
        return;
      }
      await this.VerifyAccountActivationStatus();
    }
    catch (error) {
      this.verificationError = this.translations.get('verification_failed');
    }
  }

  async VerifyAccountActivationStatus() {
    this.verificationError = null;

    try {
      await AccountActivationRepository.verifyAccountActivationStatus(this.verificationCode);

      // If no error, the account exists but is not activated yet
      await this.verifyUser();

    }
    catch (error) {
      let errorMessage = 'Activation failed. The verification code may be invalid, expired, or the account is already active.';
      const axiosError = error as AxiosError;
      if (axiosError.response && axiosError.response.data && axiosError.response.data.message) {
        errorMessage = axiosError.response.data.message;
      }

      this.verificationError = errorMessage;
    }
  }

  async verifyUser() {
    if (!this.verificationCode) {
      this.verificationError = 'Verification code is required';
      return;
    }

    this.verificationError = null;

    try {
      // Axios will follow the backend 302 redirect automatically
      await AccountActivationRepository.verifyEmail(
        this.verificationCode,
        this.signupDto.selectedPackage || null,
        this.signupDto.subscriptionRecurrence || null
      );

      this.$snotify.success(this.translations.get("verification_success_text"));
    }
    catch (error) {
      let errorMessage = 'Activation failed. The verification code may be invalid or expired.';

      const axiosError = error as AxiosError;
      if (axiosError.response && axiosError.response.data) {
        const serverMessage = (axiosError.response.data as any).message;
        if (serverMessage) {
          errorMessage = serverMessage;
        }
      }

      this.verificationError = errorMessage;
      this.isVerificationMode = false;
      await this.$router.push("/");
    }
  }

  async handleResendCode() {
    try {
      if((this.user.email != "" || this.signupDto.ownerEmail != "") && !this.isResendDisabled) {
        // Disable button and start timer.
        this.isResendDisabled = true;
        this.resendCountdown = 60;

        // Start the countdown timer
        this.resendTimer = window.setInterval(() => {
          this.resendCountdown--;
          if (this.resendCountdown <= 0) {
            this.isResendDisabled = false;
            if (this.resendTimer !== null) {
              clearInterval(this.resendTimer);
              this.resendTimer = null;
            }
          }
        }, 1000);

        let userEmail = this.signupDto.ownerEmail != "" ? this.signupDto.ownerEmail : this.user.email;
        const params = new URLSearchParams({email: userEmail});
        params.append('shouldSkipFreeTrial', this.isFreeTrialMode ? "true" : "false");
        await AccountActivationRepository.resendVerificationCode(
          userEmail,
          this.signupDto.shouldSkipFreeTrial
        );

        this.$snotify.success(this.translations.get('verification_code_sent'));
      }
    }
    catch (error) {
      this.verificationError = this.translations.get('verification_code_request_failed');
      // Enable the button in case of error
      this.isResendDisabled = false;
      if (this.resendTimer !== null) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    }
  }

  handleInputChange(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Ensure only digits are allowed
    if (DIGIT_ONLY_REGEX.test(value)) {
      this.verificationCharacters[index] = value;

      // Automatically focus on the next input box if value is entered
      if (value && index < 5) {
        const nextInput = input.nextElementSibling as HTMLInputElement;
        if (nextInput) {
          nextInput.focus();
        }
      }

      // Update verificationCode
      this.updateVerificationCode();
    }
    else {
      this.verificationCharacters[index] = "";
    }
  }

  handleBackspace(index: number): void {
    if (!this.verificationCharacters[index] && index > 0) {
      // Cast to HTMLInputElement to access focus method
      const prevInput = document.querySelectorAll(".code-input")[index - 1] as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }
    this.updateVerificationCode();
  }

  updateVerificationCode() {
    this.verificationCode = this.verificationCharacters.join('');
  }

  attemptLogin() {
    this.$validator.validateAll("login").then((result) => {
      if (result) {
        this.$snotify.async(
          this.translations.get("login_authenticating"),
          () => <Promise<Snotify>>new Promise((resolve, reject) => {
            this.$store
              .dispatch(LOGIN_USER, this.user)
              .then((response) => {
                let message: string = "";
                if (
                  [
                    UserTypes.Administrator,
                    UserTypes.SuperAdministrator,
                    UserTypes.Helpdesk,
                  ].includes(response.data.result.userType)
                ) {
                  message = "Welcome!";
                  router.push("/administration");
                }
                else {
                  message = `${this.translations.get(
                    "welcome",
                    this.$store.getters.getUserDetails.firstName
                  )}`;
                  router.push("/");
                }

                resolve({
                  body: message,
                });
              })
              .catch(() => {
                reject({
                  body: this.$store.getters.getAuthMessage,
                });

                // Check if this is a 403 Forbidden response, which indicates
                // the user needs to activate their email
                if (this.$store.getters.getAuthState === AuthStates.Forbidden) {
                  this.isVerificationMode = true;
                  this.verificationError = 'Please verify your email address to continue.';
                }
              });
          }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  handlePhoneValidation(response : any){
    this.invalidPhone = !response.isValid;
  }

  toggleCreateMode() {
    const routeName = this.$route.name;
    if (routeName === "create" || routeName === "freetrial") {
      this.$router.push({ path: "login" });
    }
    else {
      this.$router.push({ path: "create" });
    }
  }

  get isCreateMode() {
    return this.$route.name === "create" ||this.$route.name === "freetrial";
  }

  get isFreeTrialMode(){
    return this.$route.name === "freetrial";
  }

  get showMessage() {
    if (this.$store.getters.getAuthState === AuthStates.Failed) return true;
  }

  get translations() {
    return this.localizationState.translations;
  }

  nameOfNutrientSource(x: any) {
    if (x === 0) {
      return this.translations.get("nutrient_source_0");
    }
    if (x === 1) {
      return this.translations.get("nutrient_source_1");
    }
    if (x === 2) {
      return this.translations.get("nutrient_source_2");
    }
  }

  get transformedNutrientSources() {
    return this.nutrientSources
      .filter((x: NutrientSourceList) => x.isEnabled)
      .map((x: NutrientSourceList) => {
        return {
          name: x.nutrientSource,
          value: x.nutrientSource,
        };
      });
  }

  fetchNutrientSources() {
    SystemConfigurationRepository.fetchNutrientSourcesList()
      .then((response) => {
        this.nutrientSources = response.data.result;
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.errorMessages &&
          error.response.errorMessages.length > 0 &&
          error.response.errorMessages[0].userVisibleMessage
        )
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else {
          this.$snotify.error(this.translations.get("network-error"));
        }
      });
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: [
        "common",
        "login",
        "customers",
        "managesubscription",
        "profile",
      ],
    });
    document.title = "Sign In or Create Account | MenuSano";

    this.getPackageInfo();
    this.fetchNutrientSources();

    SubscriptionRepository.getSubscriptionSystemSettings()
      .then((response) => {
        this.trialPeriodInDays = response.data.result.trialDays;
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.errorMessages &&
          error.response.errorMessages.length > 0 &&
          error.response.errorMessages[0].userVisibleMessage
        )
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else {
          this.$snotify.error(this.translations.get("network-error"));
          this.trialPeriodInDays = 0;
        }
      });
    GeneralRepository.listCountries()
      .then((response) => {
        this.countries = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    GeneralRepository.listIndustries()
      .then((response) => {
        this.industriesList = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  // Lifecycle hook to clean up timer
  beforeDestroy() {
    if (this.resendTimer !== null) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

}
export default Login;
